import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Grid,
    Typography
} from "@mui/material/";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import makeStyles from "@mui/styles/makeStyles";
import actions from "../../../../Store/actions";
import { strings } from "../../../../common/i18n";
import { helpers } from "../../../../common/lib";
import { routePrefix } from "../../Routes";
import AssetHistory from "./AssetHistory";
import AuditImages from "../AuditImages";
import Layout from "../Layout";
import Toolbar from "../Toolbar";
import AssetMatch from "./AssetMatch";
import AssetMatches from "./AssetMatches";
import Detail from "./Detail";
import Resolve from "./Resolve";
import ErnestDetail from "../AuditImages/ErnestDetail";
import styles from "./styles";
import useUnexpectedAssetHistory from "./useUnexpectedAssetHistory";
import { useAuditStatus } from "../../../../common/hooks";
import { logToSentry } from "../../../../config/Sentry";

const mapImage = (item: any) => item.image;

/**
 * Alt Asset Component
 */
export default () => {
    const [acceptedMatch, setAcceptedMatch] = useState(null);
    const [displayAssetMatch, setDisplayAssetMatch] = useState(false);
    const [displayResolve, setDisplayResolve] = useState(false);
    const [assetMatch, setAssetMatch] = useState(null);

    const { altAssets, auditReview } = useSelector((state) => state);
    /** auditClose id is 6 */
    const auditCloseId = 6;
    const closedAudit = useAuditStatus().id === auditCloseId ? true : false;

    const classes = makeStyles(styles)();
    const dispatch = useDispatch();
    let navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const { altAssetId } = params;

    const data = useMemo(() => {
        if (altAssetId && altAssets.data.length) {
            const next = helpers.getListItemForId(altAssets, altAssetId);

            if (Object.keys(next).length > 0) {
                if (next.auditServices?.length) {
                    next.altAssetImages.forEach((altAssetImage) => {
                        const services = next.auditServices.filter(
                            (service) =>
                                service.auditImageId === altAssetImage.image.id
                        );
                        altAssetImage.image.services = services;
                    });
                }
                return {
                    ...next,
                    auditImages: next.altAssetImages.map(mapImage)
                };
            }
        }
        return null;
    }, [altAssetId, altAssets]);

    useEffect(() => {
        if (data && data.assetAuxiliary !== null) {
            const next = data.assetMatches.find(
                (item) =>
                    item.auxiliary &&
                    item.auxiliary.id === data.assetAuxiliary.id
            );
            next && setAcceptedMatch(next);
        }
    }, [data]);

    const handleNavigate = (previous: boolean) => () => {
        try {
            if (params.altAssetId) {
                const id = Number(params.altAssetId);
                const { ids } = auditReview.altAssets;
                let index = ids.findIndex((i) => i === id);
                if (previous) {
                    index = index > 0 ? index - 1 : ids.length - 1;
                } else {
                    index = index < ids.length - 1 ? index + 1 : 0;
                }
                const next = ids[index];
                const { pathname } = location;
                navigate(
                    pathname.replace(/alt-assets\/\d+/, `alt-assets/${next}`)
                );
            }
            setAcceptedMatch(null);
            setAssetMatch(null);
            setDisplayAssetMatch(false);
            setDisplayResolve(false);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On navigating alt asset:", { error });
            dispatch(
                actions.toast.create.error(
                    strings("errors.onNavigatingAltAsset")
                )
            );
        }
    };

    const onAssetMatchHide = () => setDisplayAssetMatch(false);
    const onAssetMatchShow = () => setDisplayAssetMatch(true);

    const onAssetMatchAccept = () => {
        try {
            setAcceptedMatch(assetMatch);
            setDisplayAssetMatch(false);
            setDisplayResolve(true);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On asset match accept:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onAssetMatchAccept"))
            );
        }
    };

    const onAssetMatchCancel = () => {
        try {
            setAcceptedMatch(null);
            setDisplayAssetMatch(false);
            setDisplayResolve(false);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On asset match cancel:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onAssetMatchCancel"))
            );
        }
    };

    const onAssetMatchValueChange = (
        nextMatch: any,
        nextDisplay: boolean = true
    ) => {
        setAssetMatch(nextMatch);
        setDisplayAssetMatch(nextDisplay);
    };

    const onResolveHide = () => setDisplayResolve(false);

    const onResolveSubmit = () => {
        try {
            if (auditReview.altAssets.ids.length <= 1) {
                const { auditId } = params;
                auditId && navigate(`/${routePrefix}/${auditId}`);
            } else {
                handleNavigate(false)();
            }
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On resolving asset:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onResolveAsset"))
            );
        }
    };

    const onResolveToggle = () => setDisplayResolve(!displayResolve);

    const RightComponent = () => {
        if (displayAssetMatch) {
            return (
                <AssetMatch
                    isMatchAccepted={Boolean(data.assetAuxiliary)}
                    isResolved={data.isResolved}
                    onAccept={onAssetMatchAccept}
                    onCancel={onAssetMatchCancel}
                    onClose={onAssetMatchHide}
                    value={assetMatch}
                />
            );
        } else if (displayResolve) {
            return (
                <Resolve
                    assetMatch={acceptedMatch}
                    onClose={onResolveHide}
                    onShowAssetMatch={onAssetMatchShow}
                    onSubmit={onResolveSubmit}
                    value={data}
                />
            );
        }
        return null;
    };

    const resolved = data && data.isResolved === true;

    const [expanded, setExpanded] = useState({
        panel1: true,
        panel2: true,
        panel3: true
    });

    const handleChange = (panel) => (event, newExpanded) => {
        try {
            switch (panel) {
                case "panel1": {
                    setExpanded((prevState) => ({
                        ...prevState,
                        panel1: !prevState.panel1
                    }));
                    break;
                }
                case "panel2": {
                    setExpanded((prevState) => ({
                        ...prevState,
                        panel2: !prevState.panel2
                    }));
                    break;
                }
                case "panel3": {
                    setExpanded((prevState) => ({
                        ...prevState,
                        panel3: !prevState.panel3
                    }));
                    break;
                }
                default: {
                    break;
                }
            }
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On selecting asset match:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onChangePanel"))
            );
        }
    };
    const historyData = useUnexpectedAssetHistory(data);

    /** If no asset is found display not found message */

    // if (!data) return <NotFound message={strings("messages.assetNotFound")} />;

    return data ? (
        <>
            <Toolbar
                onNext={handleNavigate(false)}
                onPrevious={handleNavigate(true)}
                value={data.id}
                values={auditReview.altAssets.ids}
            />
            <Layout
                LeftComponent={
                    <>
                        <Accordion
                            className={classes.accordionContainer}
                            expanded={expanded.panel1}
                            onChange={handleChange("panel1")}
                        >
                            <AccordionSummary
                                className={classes.accordionHeader}
                                expandIcon={
                                    expanded.panel1 === true ? (
                                        <RemoveIcon />
                                    ) : (
                                        <AddIcon />
                                    )
                                }
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                            >
                                <Typography variant="h6">
                                    {strings("assetDetails")}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Detail altAsset={data} />
                            </AccordionDetails>
                        </Accordion>
                        <Accordion
                            className={classes.accordionContainer}
                            expanded={expanded.panel2}
                            onChange={handleChange("panel2")}
                        >
                            <AccordionSummary
                                className={classes.accordionHeader}
                                expandIcon={
                                    expanded.panel2 === true ? (
                                        <RemoveIcon />
                                    ) : (
                                        <AddIcon />
                                    )
                                }
                                aria-controls="panel2a-content"
                                id="panel2a-header"
                            >
                                <Typography variant="h6">
                                    {strings("possibleAssetMatches")}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                                className={classes.accordionDetails}
                            >
                                <AssetMatches
                                    altAsset={data}
                                    readonly={displayResolve}
                                    onValueChange={onAssetMatchValueChange}
                                    value={assetMatch}
                                />
                            </AccordionDetails>
                        </Accordion>
                        <Accordion
                            className={classes.accordionContainer}
                            expanded={expanded.panel3}
                            onChange={handleChange("panel3")}
                        >
                            <AccordionSummary
                                expandIcon={
                                    expanded.panel3 === true ? (
                                        <RemoveIcon />
                                    ) : (
                                        <AddIcon />
                                    )
                                }
                                aria-controls="panel3a-content"
                                id="panel3a-header"
                                className={classes.accordionHeader}
                            >
                                <Typography variant="h6">
                                    {strings("history")}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                                className={classes.accordionDetails}
                            >
                                {historyData.length > 0 &&
                                    historyData.map((item, idx) => (
                                        <AssetHistory assetHistory={item} />
                                    ))}
                            </AccordionDetails>
                        </Accordion>
                    </>
                }
                RightComponent={
                    RightComponent() === null ? (
                        <AuditImages
                            altAssetId={data.id}
                            altAssetImageId={data.id}
                            identifiers={{
                                1: data.identifier1,
                                2: data.identifier2,
                                3: data.identifier3,
                                4: data.identifier4
                            }}
                            value={data.auditImages}
                        />
                    ) : (
                        <RightComponent />
                    )
                }
                ActionComponent={
                    <>
                        <Grid container spacing={3}>
                            <Grid item xs={12} style={{ padding: "3rem" }}>
                                <Button
                                    color="primary"
                                    disableElevation={
                                        !(displayAssetMatch || displayResolve)
                                    }
                                    className={classes.buttonBorderRadius}
                                    disabled={displayAssetMatch || closedAudit}
                                    fullWidth
                                    onClick={onResolveToggle}
                                    size="large"
                                    variant="contained"
                                >
                                    {displayResolve ? (
                                        <ArrowDropUp
                                            className={classes.iconLeft}
                                        />
                                    ) : (
                                        <ArrowDropDown
                                            className={classes.iconLeft}
                                        />
                                    )}
                                    {strings(resolved ? "resolved" : "resolve")}
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                }
            />
        </>
    ) : null;
};
